import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Input";
import { cn } from "@/lib/cn";
import { fmtDate } from "@/lib/briefs";
import { STATUSES, useAllFeedback, useManageFeedback, type FeedbackAdminItem, type FeedbackStatus } from "@/lib/feedback";

/** Managers' triage: list all, filter by status and category, set status, reply. */
export function FeedbackTriage() {
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const data = useAllFeedback({ status, category });
  const pill = (active: boolean) =>
    cn("rounded-full px-3 py-1 text-xs", active ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground hover:text-foreground");

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <button className={pill(status === "")} onClick={() => setStatus("")}>
          All
        </button>
        {STATUSES.map((s) => (
          <button key={s} className={pill(status === s)} onClick={() => setStatus(s)}>
            {s}
            {s === "New" && data.data ? ` (${data.data.newCount})` : ""}
          </button>
        ))}
        <span className="mx-1 text-muted-foreground text-xs">·</span>
        {["Bug", "Idea", "Question", "Other"].map((c) => (
          <button key={c} className={pill(category === c)} onClick={() => setCategory(category === c ? "" : c)}>
            {c}
          </button>
        ))}
      </div>
      {data.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : data.isError ? (
        <p className="text-destructive text-sm">Could not load feedback.</p>
      ) : data.data.feedback.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing here.</p>
      ) : (
        <ul className="space-y-2">
          {data.data.feedback.map((f) => (
            <Row key={f.id} item={f} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ item }: { item: FeedbackAdminItem }) {
  const manage = useManageFeedback();
  const [remark, setRemark] = useState(item.adminRemark ?? "");
  return (
    <li className="bg-card border-border rounded-lg border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="font-medium">{item.category}</span>
        <span className="text-muted-foreground">
          {item.raiserName ? `${item.raiserName} · ` : ""}
          {item.raiserEmail}
        </span>
        <span className="text-muted-foreground">{fmtDate(item.createdAt)}</span>
        {item.page ? <span className="text-muted-foreground font-mono">{item.page}</span> : null}
        {!item.notifiedAt ? <span className="text-status-needs-more">email pending</span> : null}
        <select
          className="bg-card border-input ml-auto rounded border px-1.5 py-1 text-xs"
          value={item.status}
          disabled={manage.isPending}
          onChange={(e) => manage.mutate({ id: item.id, status: e.target.value as FeedbackStatus })}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <p className="mt-2 whitespace-pre-wrap">{item.body}</p>
      <div className="mt-2 flex items-start gap-2">
        <Textarea value={remark} onChange={(e) => setRemark(e.target.value)} className="min-h-[40px]" placeholder="Reply to the person (they are told in-app and by email)." />
        <Button size="sm" variant="outline" disabled={manage.isPending || remark.trim() === (item.adminRemark ?? "")} onClick={() => manage.mutate({ id: item.id, adminRemark: remark.trim() || null })}>
          Reply
        </Button>
      </div>
    </li>
  );
}
